import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Field,
  Input,
  Option,
} from '@fluentui/react-components';
import { type UseMutationResult } from '@tanstack/react-query';
import { type TaxReturn } from '../../../types';

export interface EditBillingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taxReturn: TaxReturn;
  updateMutation: UseMutationResult<unknown, Error, Partial<TaxReturn>>;
}

export function EditBillingDialog({
  open,
  onOpenChange,
  taxReturn,
  updateMutation,
}: EditBillingDialogProps) {
  const [invoiceStatus, setInvoiceStatus] = useState('not_generated');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [preparerBillAmount, setPreparerBillAmount] = useState('');

  useEffect(() => {
    if (open && taxReturn) {
      setInvoiceStatus(taxReturn.clientInvoice?.status || 'not_generated');
      setInvoiceAmount(taxReturn.clientInvoice?.amount?.toString() ||
                       taxReturn.extractedInvoiceData?.totalAmount?.toString() || '');
      setInvoiceNumber(taxReturn.clientInvoice?.invoiceNumber || '');
      setPreparerBillAmount(taxReturn.preparerBillAmount?.toString() || '');
    }
  }, [open, taxReturn]);

  const handleSave = () => {
    const updates: Partial<TaxReturn> = {
      clientInvoice: {
        status: invoiceStatus as 'not_generated' | 'generated' | 'paid',
        amount: invoiceAmount ? parseFloat(invoiceAmount) : undefined,
        invoiceNumber: invoiceNumber || undefined,
        generatedDate: invoiceStatus !== 'not_generated' ? new Date().toISOString() : undefined,
        paidDate: invoiceStatus === 'paid' ? new Date().toISOString() : undefined,
      },
      preparerBillAmount: preparerBillAmount ? parseFloat(preparerBillAmount) : undefined,
    };

    updateMutation.mutate(updates, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Update Billing & Payment</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Field label="Invoice Status">
                <Dropdown
                  value={invoiceStatus === 'paid' ? 'Paid' : invoiceStatus === 'generated' ? 'Generated' : 'Not Generated'}
                  selectedOptions={invoiceStatus ? [invoiceStatus] : []}
                  onOptionSelect={(_, data) => setInvoiceStatus(data.optionValue as string)}
                  style={{ width: '100%' }}
                >
                  <Option value="not_generated">Not Generated</Option>
                  <Option value="generated">Generated</Option>
                  <Option value="paid">Paid</Option>
                </Dropdown>
              </Field>
              <Field label="Invoice Amount ($)">
                <Input
                  type="number"
                  value={invoiceAmount}
                  onChange={(_, data) => setInvoiceAmount(data.value)}
                  placeholder="0.00"
                />
              </Field>
              <Field label="Invoice Number">
                <Input
                  value={invoiceNumber}
                  onChange={(_, data) => setInvoiceNumber(data.value)}
                  placeholder="INV-2026-001"
                />
              </Field>
              <Field label="Preparer Fee ($)">
                <Input
                  type="number"
                  value={preparerBillAmount}
                  onChange={(_, data) => setPreparerBillAmount(data.value)}
                  placeholder="0.00"
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={updateMutation.isPending}
              onClick={handleSave}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
